import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Shield, AlertTriangle, Ban, Flag, Mail, Loader2 } from "lucide-react";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";

const SafetyStandardsPage = () => {
  const navigate = useNavigate();
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportDescription, setReportDescription] = useState("");
  const [reportedUsername, setReportedUsername] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!reportDescription.trim()) {
      toast.error("Please describe your concern");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/report/child-safety`, {
        description: reportDescription,
        reported_username: reportedUsername || null,
        reporter_email: reporterEmail || null
      });
      toast.success("Report submitted. Thank you for helping keep our community safe.");
      setShowReportForm(false);
      setReportDescription("");
      setReportedUsername("");
      setReporterEmail("");
    } catch (e) {
      toast.error("Failed to submit report. Please try again or email admin@pourcircle.net directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center justify-between px-4 h-14">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/")}
            className="text-white/70 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold text-white">Safety Standards</h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="px-6 py-8 max-w-3xl mx-auto space-y-8">
        {/* Title */}
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            PourCircle Safety Standards
          </h1>
          <p className="text-white/60">
            Our commitment to user safety and child protection
          </p>
          <p className="text-white/40 text-sm mt-2">
            Last updated: April 2026
          </p>
        </div>

        {/* Age Requirement */}
        <div className="glass-card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-secondary" />
            Age Requirement
          </h2>
          <p className="text-white/80">
            PourCircle is strictly for users who are <strong>21 years of age or older</strong>. 
            This app is designed for adults of legal drinking age and involves content related to 
            bars, bartenders, and alcoholic beverages.
          </p>
          <p className="text-white/80">
            We do not knowingly collect or solicit personal information from anyone under 21 years 
            of age. If we learn that we have collected personal information from a minor, we will 
            delete that information immediately.
          </p>
        </div>

        {/* Zero Tolerance Policy */}
        <div className="glass-card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Ban className="w-5 h-5 text-red-400" />
            Zero Tolerance Policy Against CSAE
          </h2>
          <p className="text-white/80">
            PourCircle maintains a <strong>zero-tolerance policy</strong> against Child Sexual Abuse 
            and Exploitation (CSAE). We are committed to preventing the use of our platform for any 
            form of child exploitation.
          </p>
          
          <h3 className="text-white font-medium mt-4">Prohibited Content and Behavior</h3>
          <p className="text-white/80">The following is strictly prohibited on PourCircle:</p>
          <ul className="space-y-2 text-white/80 ml-4">
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-red-400 mt-2 flex-shrink-0"></span>
              Any content depicting, promoting, or facilitating child sexual abuse or exploitation
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-red-400 mt-2 flex-shrink-0"></span>
              Sharing, distributing, or soliciting child sexual abuse material (CSAM)
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-red-400 mt-2 flex-shrink-0"></span>
              Grooming behavior or inappropriate contact with minors
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-red-400 mt-2 flex-shrink-0"></span>
              Sextortion or any form of sexual coercion
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-red-400 mt-2 flex-shrink-0"></span>
              Trafficking or exploitation of any kind
            </li>
          </ul>
        </div>

        {/* Enforcement */}
        <div className="glass-card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Enforcement Measures
          </h2>
          <p className="text-white/80">
            When we identify or receive reports of CSAE-related content or behavior, we take 
            immediate action:
          </p>
          <ul className="space-y-2 text-white/80 ml-4">
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
              <strong>Immediate removal</strong> of violating content
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
              <strong>Permanent ban</strong> of the offending account
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
              <strong>Reporting to authorities</strong> including the National Center for Missing 
              & Exploited Children (NCMEC) and relevant law enforcement agencies
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
              <strong>Preservation of evidence</strong> for law enforcement investigations
            </li>
          </ul>
        </div>

        {/* Reporting */}
        <div className="glass-card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Flag className="w-5 h-5 text-orange-400" />
            How to Report
          </h2>
          <p className="text-white/80">
            If you encounter any content or behavior that violates these safety standards, 
            please report it immediately:
          </p>
          <ul className="space-y-2 text-white/80 ml-4">
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-orange-400 mt-2 flex-shrink-0"></span>
              <strong>In-app reporting:</strong> Use the Report button (flag icon) on any user profile
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-orange-400 mt-2 flex-shrink-0"></span>
              <strong>Email:</strong> Contact us at <a href="mailto:admin@pourcircle.net" className="text-primary hover:underline">admin@pourcircle.net</a>
            </li>
          </ul>
          <p className="text-white/60 text-sm mt-4">
            All reports are reviewed promptly and handled with strict confidentiality.
          </p>
        </div>

        {/* Child Safety Report Form */}
        <div className="glass-card p-6 space-y-4 border-2 border-red-500/30">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Report a Child Safety Concern
          </h2>
          <p className="text-white/80">
            If you have concerns about child safety or exploitation, please report immediately. 
            All reports are treated with the highest priority and confidentiality.
          </p>
          
          {!showReportForm ? (
            <Button 
              onClick={() => setShowReportForm(true)}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              data-testid="show-child-safety-form-btn"
            >
              <Flag className="w-4 h-4 mr-2" />
              Report Child Safety Concern
            </Button>
          ) : (
            <form onSubmit={handleSubmitReport} className="space-y-4">
              <div>
                <label className="block text-white/80 text-sm mb-2">
                  Describe your concern <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  placeholder="Please provide as much detail as possible about the concern..."
                  className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-red-400 resize-none"
                  rows={4}
                  required
                  data-testid="child-safety-description"
                />
              </div>
              
              <div>
                <label className="block text-white/80 text-sm mb-2">
                  Username of concern <span className="text-red-400">*</span>
                </label>
                <Input
                  value={reportedUsername}
                  onChange={(e) => setReportedUsername(e.target.value)}
                  placeholder="@username"
                  className="input-dark"
                  required
                  data-testid="child-safety-username"
                />
              </div>
              
              <div>
                <label className="block text-white/80 text-sm mb-2">
                  Your email for follow-up (optional)
                </label>
                <Input
                  type="email"
                  value={reporterEmail}
                  onChange={(e) => setReporterEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="input-dark"
                  data-testid="child-safety-email"
                />
              </div>
              
              <div className="flex gap-3">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setShowReportForm(false)}
                  className="flex-1 border-white/20 text-white"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={submitting || !reportDescription.trim() || !reportedUsername.trim()}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="submit-child-safety-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    "Submit Report"
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* Contact */}
        <div className="glass-card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Contact Us
          </h2>
          <p className="text-white/80">
            For questions about our safety standards or to report concerns:
          </p>
          <p className="text-white/80">
            <strong>Email:</strong>{" "}
            <a href="mailto:admin@pourcircle.net" className="text-primary hover:underline">
              admin@pourcircle.net
            </a>
          </p>
        </div>

        {/* Footer */}
        <div className="text-center text-white/40 text-sm py-4">
          <p>PourCircle is committed to maintaining a safe platform for all users.</p>
        </div>
      </main>
    </div>
  );
};

export default SafetyStandardsPage;
